import { useEffect, useMemo, useState } from "react"
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query"
import { SiteHeader } from "@/components/site-header"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog"
import { Pagination } from "@/components/ui/pagination"
import { authorizedFetch } from "@/lib/api"
import { usePermissions } from "@/hooks/use-permissions"
import { FilterComboboxLabeled } from "./SmartGTComponents"
import { ConnectCrtsDialog } from "./ConnectCrtsDialog"
import type { ConnectCrtPreview } from "./ConnectCrtsDialog"
import { fmtDate } from "./utils"
import type { CrtRow, CrtsResponse, ExportRules } from "./types"
import { UserIcon, PlusIcon, XIcon, TruckIcon, DownloadIcon, AlertTriangleIcon, ExternalLinkIcon, Trash2Icon } from "lucide-react"
import { Link } from "react-router-dom"

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3001"
const PAGE_SIZE = 25

// ─── Default date range: 25th of previous month → 25th of current month ──────
function defaultDateRange(): { from: string; to: string } {
  const now = new Date()
  const year = now.getFullYear()
  const month = now.getMonth() // 0-based
  const from = new Date(year, month - 1, 25)
  const to   = new Date(year, month,     25)
  const fmt  = (d: Date) => d.toISOString().slice(0, 10)
  return { from: fmt(from), to: fmt(to) }
}

// ─── Default export rules (shared with SmartGTPage via localStorage) ──────────
function loadExportRules(): ExportRules {
  try {
    const stored = localStorage.getItem("sccr:exportRules")
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<ExportRules>
      return {
        cargoData: { groupByCrt: parsed.cargoData?.groupByCrt ?? true },
        tripData: {
          mergeByDriver:     parsed.tripData?.mergeByDriver    ?? true,
          mergeMaxDaysDiff:  parsed.tripData?.mergeMaxDaysDiff ?? 2,
          replaceTJClient:   parsed.tripData?.replaceTJClient  ?? true,
          applyFleteroRules: parsed.tripData?.applyFleteroRules ?? true,
        },
      }
    }
  } catch { /* ignore */ }
  return {
    cargoData: { groupByCrt: true },
    tripData:  { mergeByDriver: true, mergeMaxDaysDiff: 2, replaceTJClient: true, applyFleteroRules: true },
  }
}

// ─── CSV parser helper (same logic as SmartGTPage) ───────────────────────────
function parseCsvRow(line: string): string[] {
  const cells: string[] = []
  let cur = "", inQ = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (inQ) {
      if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++ }
      else if (ch === '"') { inQ = false }
      else { cur += ch }
    } else {
      if (ch === '"') { inQ = true }
      else if (ch === ",") { cells.push(cur); cur = "" }
      else { cur += ch }
    }
  }
  cells.push(cur)
  return cells
}

// ─── Status badge ─────────────────────────────────────────────────────────────
function CrtStatusBadge({ status }: { status: string }) {
  const cls =
    status === "Valorizado"       ? "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400"
    : status === "En valorización" ? "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400"
    : status === "Confirmado"      ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"
    : status === "Excluidos"       ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
    : "bg-muted text-muted-foreground"
  return (
    <Badge className={`${cls} hover:${cls} text-[10px] px-1.5 py-0`}>
      {status}
    </Badge>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function DriverCrtsPage() {
  const qc      = useQueryClient()
  const { can } = usePermissions()

  const { from: defaultFrom, to: defaultTo } = useMemo(defaultDateRange, [])

  // ── Filter state ──────────────────────────────────────────────────────────
  const [driver,   setDriver]   = useState("")
  const [dateFrom, setDateFrom] = useState(defaultFrom)
  const [dateTo,   setDateTo]   = useState(defaultTo)
  const [page,     setPage]     = useState(1)

  // ── Selection state ───────────────────────────────────────────────────────
  const [selectedIds,    setSelectedIds]    = useState<Set<string>>(new Set())
  const [connectOpen,    setConnectOpen]    = useState(false)
  const [connectSwapped, setConnectSwapped] = useState(false)
  const [connectError,   setConnectError]   = useState<string | null>(null)

  // ── Export state ──────────────────────────────────────────────────────────
  const [exportPreview, setExportPreview] = useState<{ csvText: string; filename: string } | null>(null)
  const [exportError,   setExportError]   = useState<string | null>(null)
  const [exportLoading, setExportLoading] = useState(false)
  const exportRules = useMemo(loadExportRules, [])

  // When the date range changes: reset page, clear selection AND clear driver
  // (the driver might have 0 trips in the new range)
  useEffect(() => {
    setPage(1)
    setSelectedIds(new Set())
    setDriver("")
  }, [dateFrom, dateTo])

  // When driver changes: reset page + clear selection
  useEffect(() => {
    setPage(1)
    setSelectedIds(new Set())
  }, [driver])

  // ── Fetch drivers with CRT count for the current date range ─────────────
  const { data: driverNamesData, isFetching: driversFetching } = useQuery({
    queryKey: ["smart-gt-driver-names", dateFrom, dateTo],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (dateFrom) params.set("from", dateFrom)
      if (dateTo)   params.set("to",   dateTo)
      const res = await authorizedFetch(`${API_URL}/api/smart-gt/driver-names?${params}`)
      return res.json() as Promise<{ names: { name: string; count: number }[] }>
    },
    staleTime: 60_000,
  })

  const driverOptions: { label: string; value: string }[] = useMemo(
    () => (driverNamesData?.names ?? []).map((d) => ({
      label: `${d.name} (${d.count})`,
      value: d.name,
    })),
    [driverNamesData],
  )

  // ── Fetch CRTs for selected driver + date range ───────────────────────────
  const { data: crtsData, isLoading } = useQuery({
    queryKey: ["smart-gt-driver-crts", driver, dateFrom, dateTo, page],
    queryFn: async () => {
      const params = new URLSearchParams({
        page:     String(page),
        pageSize: String(PAGE_SIZE),
        sortBy:   "collection_arrival",
        sortDir:  "asc",
      })
      if (driver)   params.set("driver",                driver)
      if (dateFrom) params.set("collectionArrivalFrom", dateFrom)
      if (dateTo)   params.set("collectionArrivalTo",   dateTo)
      const res = await authorizedFetch(`${API_URL}/api/smart-gt/crts?${params}`)
      return res.json() as Promise<CrtsResponse>
    },
    enabled: !!driver,
    placeholderData: keepPreviousData,
  })

  const rows: CrtRow[] = crtsData?.data ?? []
  const total          = crtsData?.total ?? 0
  const totalPages     = crtsData?.totalPages ?? 1

  // ── Delete CRT ───────────────────────────────────────────────────────────
  const [deletingCrt, setDeletingCrt] = useState<CrtRow | null>(null)

  const deleteCrtMut = useMutation({
    mutationFn: (id: string) =>
      authorizedFetch(`${API_URL}/api/smart-gt/crts/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["smart-gt-driver-crts"] })
      qc.invalidateQueries({ queryKey: ["smart-gt-crts"] })
      qc.invalidateQueries({ queryKey: ["smart-gt-driver-names"] })
      setDeletingCrt(null)
    },
    onError: (err: Error) => alert(err.message),
  })

  // ── Connect CRTs mutation ─────────────────────────────────────────────────
  const connectCrtMut = useMutation({
    mutationFn: ({ firstCrtId, secondCrtId }: { firstCrtId: string; secondCrtId: string }) =>
      authorizedFetch(`${API_URL}/api/smart-gt/crts/connect`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ firstCrtId, secondCrtId }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["smart-gt-driver-crts"] })
      qc.invalidateQueries({ queryKey: ["smart-gt-crts"] })
      setConnectOpen(false)
      setConnectSwapped(false)
      setConnectError(null)
      setSelectedIds(new Set())
    },
    onError: (err: Error) => setConnectError(err.message),
  })

  // ── Export trip-data ──────────────────────────────────────────────────────
  async function handleExportTrip() {
    setExportError(null)
    setExportLoading(true)
    try {
      const res = await authorizedFetch(`${API_URL}/api/smart-gt/export/trip-data`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ids: [...selectedIds],
          options: {
            cargoData: { groupByCrt: exportRules.cargoData.groupByCrt },
            tripData:  {
              mergeByDriver:     exportRules.tripData.mergeByDriver,
              mergeMaxDaysDiff:  exportRules.tripData.mergeMaxDaysDiff,
              replaceTJClient:   exportRules.tripData.replaceTJClient,
              applyFleteroRules: exportRules.tripData.applyFleteroRules,
            },
          },
        }),
      })
      const disposition = res.headers.get("Content-Disposition") ?? ""
      const match       = disposition.match(/filename="([^"]+)"/)
      const filename    = match?.[1] ?? "trip-data-export.csv"
      const csvText     = await res.text()
      setExportPreview({ csvText, filename })
    } catch (err) {
      setExportError(err instanceof Error ? err.message : "Error al exportar")
    } finally {
      setExportLoading(false)
    }
  }

  function handleDownloadCsv() {
    if (!exportPreview) return
    const blob = new Blob([exportPreview.csvText], { type: "text/csv;charset=utf-8;" })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement("a")
    a.href     = url
    a.download = exportPreview.filename
    a.click()
    URL.revokeObjectURL(url)
  }

  function handleDownloadXls() {
    if (!exportPreview) return
    const lines = exportPreview.csvText.trim().split("\n")
    const esc   = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    const headers = parseCsvRow(lines[0] ?? "")
    const dataRows = lines.slice(1).map(parseCsvRow)
    let html = `<!DOCTYPE html><html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8"></head><body><table>`
    html += "<thead><tr>" + headers.map(h => `<th>${esc(h)}</th>`).join("") + "</tr></thead>"
    html += "<tbody>" + dataRows.map(r => `<tr>${r.map(c => `<td>${esc(c)}</td>`).join("")}</tr>`).join("") + "</tbody>"
    html += "</table></body></html>"
    const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8;" })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement("a")
    a.href     = url
    a.download = exportPreview.filename.replace(/\.csv$/i, ".xls")
    a.click()
    URL.revokeObjectURL(url)
  }

  // ── Selection helpers ─────────────────────────────────────────────────────
  const allSelected  = rows.length > 0 && selectedIds.size === rows.length
  const someSelected = selectedIds.size > 0 && !allSelected

  const hasWarningsSelected = useMemo(
    () => rows.some(r => selectedIds.has(r.id) && (r.warningCount ?? 0) > 0),
    [rows, selectedIds],
  )
  const warningsSelectedCount = useMemo(
    () => rows.reduce((acc, r) => acc + (selectedIds.has(r.id) && (r.warningCount ?? 0) > 0 ? 1 : 0), 0),
    [rows, selectedIds],
  )

  function toggleSelectAll() {
    setSelectedIds(allSelected ? new Set() : new Set(rows.map(r => r.id)))
  }

  function toggleSelect(id: string) {
    setSelectedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  // ── Ordered pair for dialog (oldest collectionArrival first) ──────────────
  const connectPairOrdered = useMemo<[ConnectCrtPreview, ConnectCrtPreview] | null>(() => {
    if (selectedIds.size !== 2) return null
    const pair = rows.filter(r => selectedIds.has(r.id))
    if (pair.length !== 2) return null
    const sorted = [...pair].sort((a, b) => {
      if (!a.collectionArrival && !b.collectionArrival) return 0
      if (!a.collectionArrival) return 1
      if (!b.collectionArrival) return -1
      return new Date(a.collectionArrival).getTime() - new Date(b.collectionArrival).getTime()
    })
    const first  = connectSwapped ? sorted[1] : sorted[0]
    const second = connectSwapped ? sorted[0] : sorted[1]
    if (!first || !second) return null
    // Resolve city fallbacks so the dialog shows (and the API stores) the effective city.
    // recipientCity can be null while unloadingCity has the value, and vice-versa for shipper/loading.
    return [
      {
        id:               first.id,
        crt:              first.crt,
        shipperCity:      first.shipperCity   ?? first.loadingCity   ?? null,
        recipientCity:    first.recipientCity ?? first.unloadingCity ?? null,
        collectionArrival: first.collectionArrival,
      },
      {
        id:               second.id,
        crt:              second.crt,
        shipperCity:      second.shipperCity   ?? second.loadingCity   ?? null,
        recipientCity:    second.recipientCity ?? second.unloadingCity ?? null,
        collectionArrival: second.collectionArrival,
      },
    ]
  }, [rows, selectedIds, connectSwapped])

  const [connectFirst, connectSecond] = connectPairOrdered ?? [null, null]

  // ── Preview table rows ────────────────────────────────────────────────────
  const previewLines   = exportPreview ? exportPreview.csvText.trim().split("\n") : []
  const previewHeaders = previewLines.length > 0 ? parseCsvRow(previewLines[0] ?? "") : []
  const previewRows    = previewLines.slice(1).map(parseCsvRow)

  return (
    <>
      <SiteHeader title="CRTs por Chofer" />
      <div className="flex flex-1 flex-col gap-4 px-4 py-6 lg:px-6">

        {/* ── Filters ────────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-end gap-3">
          {/* Dates — left side */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="date-from" className="text-xs">Desde</Label>
            <Input
              id="date-from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="h-9 w-40 text-sm"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="date-to" className="text-xs">Hasta</Label>
            <Input
              id="date-to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="h-9 w-40 text-sm"
            />
          </div>

          {/* Driver autocomplete — right side, grows to fill remaining space */}
          <div className="flex flex-col gap-1.5 min-w-[280px] flex-1">
            <Label className="text-xs flex items-center gap-1.5">
              <UserIcon className="size-3.5" />
              Chofer
              {driversFetching && (
                <span className="text-muted-foreground/60 font-normal">(actualizando…)</span>
              )}
            </Label>
            <FilterComboboxLabeled
              value={driver}
              onChange={setDriver}
              options={driverOptions}
              placeholder={driverOptions.length === 0 && !driversFetching
                ? "Sin choferes en el período"
                : "Buscar chofer…"}
            />
          </div>
        </div>

        {/* ── Empty state ───────────────────────────────────────────────── */}
        {!driver ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-2">
            <UserIcon className="size-8 opacity-30" />
            <p className="text-sm">Seleccioná un chofer para ver sus CRTs</p>
          </div>
        ) : (
          <>
            {/* ── Selection action bar ─────────────────────────────────── */}
            {selectedIds.size > 0 && (
              <div className="sticky top-0 z-20 flex flex-wrap items-center gap-2 rounded-lg border bg-background/95 backdrop-blur-sm px-4 py-2.5 shadow-sm">
                <span className="text-sm font-medium text-muted-foreground shrink-0">
                  {selectedIds.size} CRT{selectedIds.size !== 1 ? "s" : ""} seleccionado{selectedIds.size !== 1 ? "s" : ""}
                </span>

                <div className="flex flex-wrap items-center gap-2 flex-1">
                  {/* Export trip-data */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 h-8"
                    disabled={hasWarningsSelected || exportLoading}
                    title={hasWarningsSelected ? "No se puede exportar: hay CRTs con problemas de mapeo" : undefined}
                    onClick={handleExportTrip}
                  >
                    <TruckIcon className="size-3.5" />
                    {exportLoading ? "Exportando…" : "Exportar datos de viaje"}
                  </Button>

                  {/* Connect CRTs — admin only */}
                  {can("write", "smartgt") && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2 h-8"
                      disabled={selectedIds.size !== 2}
                      title={
                        selectedIds.size < 2 ? `Seleccioná exactamente 2 CRTs (${selectedIds.size}/2)` :
                        selectedIds.size > 2 ? "Seleccioná solo 2 CRTs" :
                        "Crear conexión entre destinos"
                      }
                      onClick={() => {
                        setConnectSwapped(false)
                        setConnectError(null)
                        setConnectOpen(true)
                      }}
                    >
                      <PlusIcon className="size-3.5" />
                      Conexión entre destinos
                      <span className={`text-[10px] px-1.5 py-0 rounded-full font-semibold ${
                        selectedIds.size === 2
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-muted-foreground"
                      }`}>
                        {selectedIds.size}/2
                      </span>
                    </Button>
                  )}

                  {/* Warning notice */}
                  {hasWarningsSelected && (
                    <div className="inline-flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400 font-medium ml-auto">
                      <AlertTriangleIcon className="size-3.5" />
                      No se puede exportar: {warningsSelectedCount} CRT{warningsSelectedCount !== 1 ? "s" : ""} con problemas de mapeo
                    </div>
                  )}

                  {/* Export error */}
                  {exportError && (
                    <span className="text-xs text-destructive ml-auto">{exportError}</span>
                  )}
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 shrink-0"
                  onClick={() => setSelectedIds(new Set())}
                  title="Limpiar selección"
                >
                  <XIcon className="size-4" />
                </Button>
              </div>
            )}

            {/* ── Table ────────────────────────────────────────────────── */}
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10 pl-3">
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected}
                        onCheckedChange={toggleSelectAll}
                        aria-label="Seleccionar todos"
                      />
                    </TableHead>
                    <TableHead className="w-[120px]">CRT</TableHead>
                    <TableHead>Origen</TableHead>
                    <TableHead>Destino</TableHead>
                    <TableHead className="w-20 text-right">KM</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead className="w-[140px]">Estado</TableHead>
                    <TableHead className="w-[120px]">Llegada a coleta</TableHead>
                    <TableHead className="w-16 text-center">Detalles</TableHead>
                    {can("write", "smartgt") && <TableHead className="w-10" />}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={can("write", "smartgt") ? 10 : 9} className="text-center py-10 text-muted-foreground text-sm">
                        Cargando…
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={can("write", "smartgt") ? 10 : 9} className="text-center py-10 text-muted-foreground text-sm">
                        Sin CRTs para este chofer en el período seleccionado
                      </TableCell>
                    </TableRow>
                  ) : rows.map((row) => {
                    const selected = selectedIds.has(row.id)

                    // ── Origin (shipperCity with loadingCity fallback) ──────
                    const usingOriginFallback = !row.shipperCity && !!row.loadingCity
                    const originCity    = row.shipperCity ?? row.loadingCity
                    const originMatchId = row.shipperCity ? row.matchedShipperCityId : row.matchedLoadingCityId

                    // ── Destination (recipientCity with unloadingCity fallback) ──
                    const destCity    = row.recipientCity ?? row.unloadingCity
                    const destCountry = row.recipientCountry ?? row.unloadingCountry
                    const destMatchId = row.recipientCity ? row.matchedRecipientCityId : row.matchedUnloadingCityId

                    // ── KM ────────────────────────────────────────────────────
                    const hasMappedCities = !!(row.matchedShipperCityId || row.matchedLoadingCityId ||
                                              row.matchedRecipientCityId || row.matchedUnloadingCityId)

                    return (
                      <TableRow
                        key={row.id}
                        className={`transition-colors cursor-pointer ${selected ? "bg-muted/60" : "hover:bg-muted/40"}`}
                        onClick={() => toggleSelect(row.id)}
                      >
                        {/* Checkbox */}
                        <TableCell className="pl-3" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selected}
                            onCheckedChange={() => toggleSelect(row.id)}
                            aria-label="Seleccionar fila"
                          />
                        </TableCell>

                        {/* CRT */}
                        <TableCell>
                          <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">
                            {row.crt}
                          </span>
                        </TableCell>

                        {/* Origen */}
                        <TableCell className="whitespace-normal">
                          {originCity ? (
                            <span className="flex flex-wrap items-center gap-1 text-sm">
                              {originMatchId ? (
                                <Link
                                  to={`/geo/cities/${originMatchId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-0.5 text-primary hover:underline"
                                  title={usingOriginFallback ? "Ciudad de carga (fallback)" : "Ciudad registrada"}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {originCity}<ExternalLinkIcon className="size-3 shrink-0 opacity-60" />
                                </Link>
                              ) : (
                                <span className="inline-flex items-center gap-0.5 text-muted-foreground" title="Ciudad no registrada">
                                  {originCity}{!usingOriginFallback && <AlertTriangleIcon className="size-3 shrink-0 text-amber-500" />}
                                </span>
                              )}
                              {usingOriginFallback && (
                                <span className="text-[10px] px-1 py-px rounded bg-red-100 text-muted-foreground" title="Ciudad tomada del campo Lugar de carga">carga</span>
                              )}
                              {originCity && row.shipperCountry && <span className="text-muted-foreground">/ {row.shipperCountry}</span>}
                            </span>
                          ) : (
                            <span className="text-muted-foreground/50 text-sm">—</span>
                          )}
                        </TableCell>

                        {/* Destino */}
                        <TableCell className="whitespace-normal">
                          {destCity ? (
                            <span className="flex flex-wrap items-center gap-1 text-sm">
                              {destMatchId ? (
                                <Link
                                  to={`/geo/cities/${destMatchId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-0.5 text-primary hover:underline"
                                  title="Ciudad registrada"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {destCity}<ExternalLinkIcon className="size-3 shrink-0 opacity-60" />
                                </Link>
                              ) : (
                                <span className="inline-flex items-center gap-0.5 text-muted-foreground" title="Ciudad no registrada">
                                  {destCity}<AlertTriangleIcon className="size-3 shrink-0 text-amber-500" />
                                </span>
                              )}
                              {destCity && destCountry && <span className="text-muted-foreground">/ {destCountry}</span>}
                            </span>
                          ) : (
                            <span className="text-muted-foreground/50 text-sm">—</span>
                          )}
                        </TableCell>

                        {/* KM */}
                        <TableCell className="text-right">
                          {row.matchedRouteId ? (
                            <Link
                              to={`/geo/routes/${row.matchedRouteId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-end gap-1 text-sm font-mono text-primary hover:underline"
                              title="Ver ruta registrada"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {row.matchedRouteKm != null ? `${row.matchedRouteKm} km` : "—"}
                              <ExternalLinkIcon className="size-3 shrink-0 opacity-60" />
                            </Link>
                          ) : hasMappedCities ? (
                            <span
                              className="inline-flex items-center justify-end gap-1 text-xs text-muted-foreground"
                              title="Ruta no registrada para estas ciudades"
                            >
                              —<AlertTriangleIcon className="size-3 shrink-0 text-amber-500" />
                            </span>
                          ) : (
                            <span className="text-muted-foreground/40 text-xs">—</span>
                          )}
                        </TableCell>

                        {/* Cliente */}
                        <TableCell className="text-sm max-w-[180px]">
                          <span className="line-clamp-2">
                            {row.client ?? <span className="text-muted-foreground/50">—</span>}
                          </span>
                        </TableCell>

                        {/* Estado */}
                        <TableCell>
                          <CrtStatusBadge status={row.crtStatus} />
                        </TableCell>

                        {/* Llegada a coleta */}
                        <TableCell className="text-sm text-muted-foreground tabular-nums">
                          {fmtDate(row.collectionArrival)}
                        </TableCell>

                        {/* Detalles */}
                        <TableCell className="text-center">
                          <Badge variant="secondary">{row.detailCount}</Badge>
                        </TableCell>

                        {/* Acciones — admin only */}
                        {can("write", "smartgt") && (
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              className="text-destructive hover:text-destructive"
                              title="Eliminar CRT"
                              onClick={() => setDeletingCrt(row)}
                            >
                              <Trash2Icon className="size-3.5" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>

            {totalPages > 1 && (
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={total}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
              />
            )}

            {!isLoading && rows.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {total} CRT{total !== 1 ? "s" : ""} encontrado{total !== 1 ? "s" : ""}
              </p>
            )}
          </>
        )}
      </div>

      {/* ── Delete CRT dialog ───────────────────────────────────────────────── */}
      <Dialog open={!!deletingCrt} onOpenChange={(o) => !o && setDeletingCrt(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Eliminar CRT</DialogTitle>
            <DialogDescription>
              ¿Eliminás el CRT{" "}
              <span className="font-mono font-semibold">{deletingCrt?.crt}</span>{" "}
              y todos sus detalles? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose>
              <Button variant="outline" disabled={deleteCrtMut.isPending}>
                Cancelar
              </Button>
            </DialogClose>
            <Button
              variant="destructive"
              disabled={deleteCrtMut.isPending}
              onClick={() => deletingCrt && deleteCrtMut.mutate(deletingCrt.id)}
            >
              {deleteCrtMut.isPending ? "Eliminando…" : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Connect CRTs Dialog ───────────────────────────────────────────────── */}
      {connectOpen && connectFirst && connectSecond && (
        <ConnectCrtsDialog
          open={connectOpen}
          onOpenChange={(v) => {
            if (!v) {
              setConnectOpen(false)
              setConnectSwapped(false)
              setConnectError(null)
            }
          }}
          firstCrt={connectFirst}
          secondCrt={connectSecond}
          onSwap={() => setConnectSwapped(s => !s)}
          onConfirm={() => {
            if (!connectFirst || !connectSecond) return
            connectCrtMut.mutate({ firstCrtId: connectFirst.id, secondCrtId: connectSecond.id })
          }}
          isPending={connectCrtMut.isPending}
          error={connectError}
        />
      )}

      {/* ── Export Preview Dialog ─────────────────────────────────────────── */}
      <Dialog open={!!exportPreview} onOpenChange={(open) => { if (!open) setExportPreview(null) }}>
        <DialogContent className="max-w-5xl w-full flex flex-col" style={{ maxHeight: "85vh" }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DownloadIcon className="size-4" />
              Preview de exportación — Datos de viaje
            </DialogTitle>
            <DialogDescription className="font-mono text-xs truncate">
              {exportPreview?.filename}
            </DialogDescription>
          </DialogHeader>

          {/* Download buttons + row count */}
          <div className="flex items-center gap-2 pb-2 border-b">
            <Button size="sm" className="gap-2" onClick={handleDownloadCsv}>
              <DownloadIcon className="size-4" /> Descargar CSV
            </Button>
            <Button size="sm" variant="outline" className="gap-2" onClick={handleDownloadXls}>
              <DownloadIcon className="size-4" /> Descargar XLS (97–2003)
            </Button>
            <span className="text-xs text-muted-foreground">
              {previewRows.length} fila{previewRows.length !== 1 ? "s" : ""}
            </span>
          </div>

          {/* CSV table preview */}
          <div className="overflow-auto flex-1 rounded border">
            <table className="text-xs w-full border-collapse">
              <thead className="sticky top-0 bg-muted z-10">
                <tr>
                  {previewHeaders.map((h, i) => (
                    <th key={i} className="border-b border-r px-2 py-1.5 text-left font-semibold whitespace-nowrap text-muted-foreground last:border-r-0">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, ri) => (
                  <tr key={ri} className={ri % 2 === 0 ? "bg-background" : "bg-muted/30"}>
                    {row.map((cell, ci) => (
                      <td key={ci} className="border-b border-r px-2 py-1 whitespace-nowrap last:border-r-0 font-mono">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
